'use client';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  Popover,
  Radio,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  BackupZeroEtlIdentityColumnField,
  BackupZeroEtlRoutingMode,
} from '@repo/api-gw-sdk';
import { useRef, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { OptionMenu } from '@/components/option-menu/option-menu';
import {
  OptionListItem,
  OptionListItemContent,
} from '@/components/shared/optionListItem';

import {
  FULLY_QUALIFIED_NAMESPACE,
  FULLY_QUALIFIED_TABLE,
  type ZeroEtlPolicyEditState,
} from './ZeroEtlPolicyCreationFlow';

const IDENTITY_COLUMN_OPTIONS = Object.values(BackupZeroEtlIdentityColumnField);

const VARIABLES_DISPLAY_NAME: Record<BackupZeroEtlIdentityColumnField, string> =
  {
    [BackupZeroEtlIdentityColumnField.BACKUP_ZERO_ETL_IDENTITY_COLUMN_FIELD_SOURCE_CLOUD]:
      'Source Cloud',
    [BackupZeroEtlIdentityColumnField.BACKUP_ZERO_ETL_IDENTITY_COLUMN_FIELD_SOURCE_ACCOUNT_ID]:
      'Source Account ID',
    [BackupZeroEtlIdentityColumnField.BACKUP_ZERO_ETL_IDENTITY_COLUMN_FIELD_SOURCE_REGION]:
      'Source Region',
    [BackupZeroEtlIdentityColumnField.BACKUP_ZERO_ETL_IDENTITY_COLUMN_FIELD_RESOURCE_ID]:
      'Resource ID',
    [BackupZeroEtlIdentityColumnField.BACKUP_ZERO_ETL_IDENTITY_COLUMN_FIELD_DATABASE_NAME]:
      'Database Name',
    [BackupZeroEtlIdentityColumnField.BACKUP_ZERO_ETL_IDENTITY_COLUMN_FIELD_SCHEMA_NAME]:
      'Schema Name',
    [BackupZeroEtlIdentityColumnField.BACKUP_ZERO_ETL_IDENTITY_COLUMN_FIELD_TABLE_NAME]:
      'Table Name',
  };

const TEMPLATE_VARIABLES = [
  '{source_cloud}',
  '{source_account_id}',
  '{source_region}',
  '{resource_id}',
  '{database_name}',
  '{schema_name}',
  '{table_name}',
];

type RoutingPolicyFields = Pick<
  ZeroEtlPolicyEditState,
  | 'routingMode'
  | 'namespaceTemplate'
  | 'tableTemplate'
  | 'identityColumns'
  | 'showAdditionalColumnsSection'
>;

interface RoutingPolicyPanelProps {
  state: ZeroEtlPolicyEditState;
  onChange: (updates: Partial<RoutingPolicyFields>) => void;
}

export const ZeroEtlRouting = ({
  state,
  onChange,
}: RoutingPolicyPanelProps) => {
  return (
    <Stack gap='40px' width='100%' minWidth={0}>
      <Stack className='px-[min(100px,5vw)]' gap='24px'>
        <Typography variant='h3'>Table mapping</Typography>
        <Typography variant='body1'>
          Define how your data is organized into Iceberg tables in the AWS Glue
          Catalog.
        </Typography>
      </Stack>
      <Box className='px-[min(100px,5vw)]'>
        <RoutingModeSelector state={state} onChange={onChange} />
      </Box>
      <AddColumnsSection state={state} onChange={onChange} />
    </Stack>
  );
};

const RoutingModeSelector = ({
  state,
  onChange,
}: {
  state: ZeroEtlPolicyEditState;
  onChange: (updates: Partial<RoutingPolicyFields>) => void;
}) => {
  const isManual =
    state.routingMode ===
    BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL;

  return (
    <Stack gap='24px' overflow='auto'>
      <Typography>Naming</Typography>
      <List disablePadding>
        <OptionListItem
          isChecked={!isManual}
          onClick={() =>
            onChange({
              routingMode:
                BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED,
            })
          }
        >
          <FormControlLabel
            checked={!isManual}
            onClick={() => {
              onChange({
                routingMode:
                  BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_FULLY_QUALIFIED,
              });
            }}
            control={<Radio sx={{ padding: '0' }} />}
            sx={{ alignItems: 'flex-start', gap: '12px' }}
            label={
              <Stack gap='4px'>
                <Typography variant='subtitle1'>Automatic</Typography>
                <Typography variant='body2'>
                  Tables are written using a deterministic path derived from the
                  source resource.
                </Typography>
              </Stack>
            }
          />

          <OptionListItemContent isExpanded={!isManual}>
            <Stack
              width='100%'
              sx={{ backgroundColor: 'background.secondary' }}
              padding='16px'
              gap='12px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <ReadOnlyField
                label='Database name'
                value={FULLY_QUALIFIED_NAMESPACE}
              />
              <ReadOnlyField label='Table name' value={FULLY_QUALIFIED_TABLE} />
            </Stack>
          </OptionListItemContent>
        </OptionListItem>
        <OptionListItem
          isChecked={isManual}
          onClick={() =>
            onChange({
              routingMode:
                BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL,
            })
          }
        >
          <FormControlLabel
            checked={isManual}
            onClick={() => {
              onChange({
                routingMode:
                  BackupZeroEtlRoutingMode.BACKUP_ZERO_ETL_ROUTING_MODE_MANUAL,
              });
            }}
            control={<Radio sx={{ padding: '0' }} />}
            sx={{ alignItems: 'flex-start', gap: '12px' }}
            label={
              <Stack gap='4px'>
                <Typography variant='subtitle1'>Custom</Typography>
                <Typography variant='body2'>
                  Define how your data is organized into databases and tables in
                  AWS Glue Catalog.
                </Typography>
              </Stack>
            }
          />
          <OptionListItemContent isExpanded={isManual}>
            <Stack
              gap='24px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <EditVariableStrip
                label='Database name'
                value={state.namespaceTemplate}
                onChange={(value) => onChange({ namespaceTemplate: value })}
                placeholder='{source_cloud}.{database_name}.{schema_name}'
              />
              <EditVariableStrip
                label='Table name'
                value={state.tableTemplate}
                onChange={(value) => onChange({ tableTemplate: value })}
                placeholder='{table_name}'
              />
            </Stack>
          </OptionListItemContent>
        </OptionListItem>
      </List>
    </Stack>
  );
};

const baseZIndex = 1;
const EditVariableStrip = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const addColumn = (column: string) => {
    const input = inputRef.current;
    const pos = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? pos;
    const next = value.slice(0, pos) + column + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      if (!input) return;
      const cursor = pos + column.length;
      input.setSelectionRange(cursor, cursor);
      input.focus();
    });
  };

  return (
    <Stack gap='4px' direction='row' alignItems='text-start' width='100%'>
      <Typography width='120px'>{label}</Typography>
      <Stack gap='8px' width='100%' sx={{ position: 'relative' }}>
        <TextField
          ref={setAnchorEl}
          fullWidth
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          size='small'
          sx={{ position: 'relative', zIndex: baseZIndex + 1 }}
          slotProps={{
            input: {
              sx: { fontFamily: 'monospace' },
            },
            htmlInput: { ref: inputRef },
          }}
        />
        <Popover
          open={isFocused}
          anchorEl={anchorEl}
          onClose={() => setIsFocused(false)}
          disablePortal
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          sx={{ zIndex: baseZIndex }}
          slotProps={{
            paper: {
              sx: {
                padding: '12px',
                marginTop: '-12px',
                marginLeft: '-12px',
                width: `${(anchorEl?.offsetWidth || 0) + 24}px`,
              },
            },
          }}
        >
          <Stack gap='24px'>
            <Box height='32px' />
            <Stack gap='12px'>
              <Typography variant='body2'>
                Click a variable to insert at cursor
              </Typography>
              <Stack gap='8px' direction='row' width='100%'>
                {TEMPLATE_VARIABLES.map((v) => (
                  <ColumnTag key={v} column={v} addColumn={addColumn} />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Popover>
        <PreviewTemplate template={value} />
      </Stack>
    </Stack>
  );
};

const ColumnTag = ({
  column,
  addColumn,
}: {
  column: string;
  addColumn: (column: string) => void;
}) => {
  return (
    <Button
      onClick={() => addColumn(column)}
      sx={{
        border: '1px solid var(--mui-palette-custom-BLUE-01)',
        color: 'var(--mui-palette-custom-BLUE-01)',
        padding: '4px 8px',

        '&:hover': {
          borderColor: 'transparent',
        },
      }}
    >
      {column}
    </Button>
  );
};

const PreviewTemplate = ({ template }: { template: string }) => {
  return (
    <Stack
      width='100%'
      sx={{ backgroundColor: 'background.secondary' }}
      padding='16px'
    >
      <ColoredVariables text={template} />
    </Stack>
  );
};

const AddColumnsSection = ({
  state,
  onChange,
}: {
  state: ZeroEtlPolicyEditState;
  onChange: (updates: Partial<RoutingPolicyFields>) => void;
}) => {
  const selectedFields = new Set(state.identityColumns.map((ic) => ic.field));

  const addField = (field: BackupZeroEtlIdentityColumnField) => {
    const updated = [
      ...state.identityColumns,
      { field, columnName: field.toLowerCase(), partition: false },
    ];
    onChange({ identityColumns: updated });
  };

  const removeField = (field: BackupZeroEtlIdentityColumnField) => {
    onChange({
      identityColumns: state.identityColumns.filter((ic) => ic.field !== field),
    });
  };

  const togglePartition = (field: BackupZeroEtlIdentityColumnField) => {
    onChange({
      identityColumns: state.identityColumns.map((ic) =>
        ic.field === field ? { ...ic, partition: !ic.partition } : ic
      ),
    });
  };

  return (
    <Stack gap='24px' paddingTop='16px'>
      <Divider />
      <Stack gap='24px' className='px-[min(100px,5vw)]'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='subtitle1'>
            Add metadata columns from resource properties
          </Typography>
          <Switch
            checked={state.showAdditionalColumnsSection}
            onChange={(_, checked) =>
              onChange({ showAdditionalColumnsSection: checked })
            }
          />
        </Stack>
        {state.showAdditionalColumnsSection && (
          <>
            <Typography variant='body2' color='text.secondary'>
              Add columns with resource properties (e.g., account, region) that
              will be added to your final tables.
            </Typography>

            {state.identityColumns.length > 0 && (
              <Stack gap='12px'>
                {state.identityColumns.map((ic) => (
                  <Stack
                    key={ic.field}
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    gap='12px'
                    padding='12px 24px'
                    border='1px solid var(--mui-palette-divider)'
                    borderRadius='4px'
                  >
                    <Stack direction='row' alignItems='center' gap='12px'>
                      <Typography>{ic.field.toLowerCase()}</Typography>
                    </Stack>
                    <Stack direction='row' alignItems='center' gap='40px'>
                      <Stack direction='row' alignItems='center' gap='12px'>
                        <Checkbox
                          checked={!!ic.partition}
                          onChange={() => togglePartition(ic.field)}
                          size='small'
                          sx={{ padding: '2px' }}
                        />
                        <Typography variant='body1'>
                          Use as partition key
                        </Typography>
                      </Stack>
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={() => removeField(ic.field)}
                      >
                        <i className='material-symbols-close-rounded' />
                      </IconButton>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
            <FieldSelector
              selectedFields={selectedFields}
              addField={addField}
            />
          </>
        )}
      </Stack>
      <Divider />
    </Stack>
  );
};

const FieldSelector = ({
  selectedFields,
  addField,
}: {
  selectedFields: Set<BackupZeroEtlIdentityColumnField>;
  addField: (field: BackupZeroEtlIdentityColumnField) => void;
}) => {
  return (
    <OptionMenu
      placement='left'
      Component={ButtonWithIcon}
      componentProps={{
        text: 'Add Column',
        icon: 'material-symbols-add-rounded',
      }}
      options={IDENTITY_COLUMN_OPTIONS.filter(
        (f) => !selectedFields.has(f)
      ).map((field) => ({
        id: field,
        text: VARIABLES_DISPLAY_NAME[field],
        menuItemProps: { onClick: () => addField(field) },
      }))}
    />
  );
};

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <Stack gap='4px' direction='row' alignItems='center'>
    <Typography width='100px' variant='body2'>
      {label}
    </Typography>
    <ColoredVariables text={value} />
  </Stack>
);

export const ColoredVariables = ({
  text,
  variant = 'subtitle1',
}: {
  text: string;
  variant?: 'subtitle1' | 'subtitle2';
}) => {
  if (!text) {
    return <Typography variant='body1'>Preview will appear here</Typography>;
  }

  const parts = text.split(/(\{[^}]*\})/g);

  return (
    <Typography
      variant={variant}
      sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
    >
      {parts.map((part, index) =>
        part.startsWith('{') && part.endsWith('}') ? (
          <span
            key={index}
            style={{ color: 'var(--mui-palette-custom-BLUE-01)' }}
          >
            {part}
          </span>
        ) : (
          part
        )
      )}
    </Typography>
  );
};
